/**
 * PAGE: LISTE DES PROSPECTS
 * Affiche toutes les soumissions de formulaires avec leurs informations enrichies
 */

import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BulkAssignUserModal,
  BulkCreateTaskModal,
  ColumnSelectorModal,
  CreateProspectModal,
  FiltersModal,
  ImportCSVModal,
  SendBulkEmailModal,
} from '../components/modals';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { ProspectDetailPanel } from '../components/panels/ProspectDetailPanel';
import { ActiveFilterChips, SavedSearchToolbar } from '../components/search';
import { TableCell } from '../components/table';
import { EnterpriseTable, SelectAllBanner } from '../components/tables';
import { ActionsCell, CheckboxCell } from '../components/tables/cells';
import { PaymentBadgeCell } from '../components/payment/PaymentBadgeCell';
import { Badge, BulkActionsBar, ConfirmDialog, ErrorState, toast } from '../components/ui';
import { CrewJoinRequestsTable } from '../components/crew/CrewJoinRequestsTable';
import { PROSPECT_CONFIG } from '../config/entities/prospect.config';
import { useUsers } from '../contexts/UsersContext';
import { useContactActions } from '../hooks/useContactActions';
import { useEntityList } from '../hooks/useEntityList';
import { useOpenFromSearch } from '../hooks/useOpenFromSearch';
import { useOrigins } from '../hooks/useOrigins';
import { usePermissions } from '../hooks/usePermissions';
import { useProducts } from '../hooks/useProducts';
import { useProspects } from '../hooks/useProspects';
import { useSearchSessionMemory } from '../hooks/useSearchSessionMemory';
import { useServerPagination } from '../hooks/useServerPagination';
import { useSupabase } from '../hooks/useSupabase';
import { generateImportConfig } from '../lib/entity-config';
import type { FilterConfig } from '../types/filters';
import type { FormSubmission } from '../types/form';
import type { PaymentStatus } from '../types/payment';
import { PAYMENT_STATUS_LABELS } from '../types/payment';
import { createSavedSearchDefinition, type SavedSearchDefinition } from '../types/saved-search';

export function ProspectsListPage() {
  const supabase = useSupabase();
  const { origins } = useOrigins();
  const { products } = useProducts();
  const { usersMap } = useUsers();
  const {
    error,
    remove,
    removeMany,
    update,
    create,
    refresh,
    fetchPaginated,
    fetchFiltered,
    fetchAllFilteredIds,
    allData,
  } = useProspects({
    mode: 'server',
  });

  // Permissions utilisateur
  const {
    role,
    canCreateEntity,
    canDeleteEntity,
    canImportData,
    canExportData,
    canDeleteBulk,
    canManageAssignments,
    canSendEmails,
  } = usePermissions();

  // Pagination server-side (gère loading et data)
  const serverPagination = useServerPagination<FormSubmission>(fetchPaginated!, {
    initialLimit: 25,
  });

  // ✅ Enrichir les données avec les assigned_user depuis le cache
  const prospects: FormSubmission[] = useMemo(() => {
    return serverPagination.data.map((submission) => {
      const assignedTo = submission.contact?.assigned_to;
      if (!assignedTo) return submission;

      const assignedUser = usersMap[assignedTo];
      return {
        ...submission,
        contact: submission.contact
          ? {
              ...submission.contact,
              assigned_user: assignedUser || null,
            }
          : undefined,
      } as FormSubmission;
    });
  }, [serverPagination.data, usersMap]);

  const loading = serverPagination.loading;

  // ✅ OPTIMISATION: Utiliser useContactActions au lieu de useContacts
  // pour avoir uniquement la fonction update sans charger tous les contacts
  const { updateContact } = useContactActions();

  const [selectedProspect, setSelectedProspect] = useState<FormSubmission | null>(null);
  const [teammate, setTeammate] = useState<FormSubmission | null>(null);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isBulkEmailModalOpen, setIsBulkEmailModalOpen] = useState(false);
  const [selectedProspectsForEmail, setSelectedProspectsForEmail] = useState<FormSubmission[]>([]);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [isBulkCreateTaskModalOpen, setIsBulkCreateTaskModalOpen] = useState(false);
  // État pour la sélection globale (Feature 4.4)
  const [isAllResultsSelected, setIsAllResultsSelected] = useState(false);
  const [isLoadingAllIds, setIsLoadingAllIds] = useState(false);
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  const [bpiFilter, setBpiFilter] = useState<
    'all' | 'without_bpi' | 'with_bpi' | 'crew_join_requests'
  >(() => {
    if (initialFilter === 'without_bpi' || initialFilter === 'with_bpi') return initialFilter;
    if (initialFilter === 'crew_join_requests' && role !== 'user') return 'crew_join_requests';
    return 'all';
  });
  // DATA-02 : les BPI auto-expirés sont masqués par défaut (toggle dans la barre d'onglets).
  const [showExpired, setShowExpired] = useState(false);
  // #234 : les pré-inscriptions d'une personne déjà en équipage sont masquées par défaut.
  const [showAlreadyInCrew, setShowAlreadyInCrew] = useState(false);
  const [crewJoinRequestsCount, setCrewJoinRequestsCount] = useState(0);

  const handleCloseSidePanel = useCallback(() => {
    setIsSidePanelOpen(false);
    setSelectedProspect(null);
    setTeammate(null);
  }, []);

  // Fonction pour trouver le coéquipier (prospect avec le même bpi_code)
  const findTeammate = useCallback(
    async (prospect: FormSubmission): Promise<FormSubmission | null> => {
      if (!prospect.bpi_code) return null;

      try {
        // maybeSingle (pas single) : l'absence de coequipier est le cas normal
        // (inscription solo, coequipier pas encore enregistre). single() leverait
        // un 406 PostgREST a chaque lookup sans match ; maybeSingle renvoie
        // data=null sans erreur. >1 ligne (anomalie data) reste une erreur -> null.
        const { data, error } = await supabase
          .from('prospects_view')
          .select('*')
          .eq('bpi_code', prospect.bpi_code)
          .neq('id', prospect.id)
          .maybeSingle();

        if (error || !data) return null;

        return data as FormSubmission;
      } catch (error) {
        console.error('Error finding teammate:', error);
        return null;
      }
    },
    [supabase]
  );

  const handleOpenSidePanel = useCallback(
    async (prospect: FormSubmission) => {
      setSelectedProspect(prospect);
      setIsSidePanelOpen(true);

      // Récupérer le coéquipier si BPI ID existe
      if (prospect.bpi_code) {
        const foundTeammate = await findTeammate(prospect);
        setTeammate(foundTeammate);
      } else {
        setTeammate(null);
      }
    },
    [findTeammate]
  );

  const filterConfigs: FilterConfig[] = useMemo(() => {
    const baseFilters: FilterConfig[] = [
      {
        field: 'form.origin.label',
        label: 'Origine',
        type: 'select',
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'],
        options: origins.map((origin) => ({
          value: origin.label,
          label: origin.label,
        })),
      },
      {
        field: 'form.product.name',
        label: 'Produit',
        type: 'select',
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'],
        options: products.map((product) => ({
          value: product.name,
          label: product.name,
        })),
      },
      {
        field: 'contact.first_name',
        label: 'Prénom',
        type: 'text',
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ],
      },
      {
        field: 'contact.last_name',
        label: 'Nom',
        type: 'text',
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ],
      },
      {
        field: 'contact.email',
        label: 'Email',
        type: 'text',
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ],
      },
      {
        field: 'contact.phone',
        label: 'Téléphone',
        type: 'text',
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ],
      },
      {
        field: 'bpi_code',
        label: 'BPI ID',
        type: 'text',
        operators: [
          'equals',
          'notEquals',
          'contains',
          'notContains',
          'startsWith',
          'notStartsWith',
          'endsWith',
          'notEndsWith',
          'isNotNull',
          'isNull',
        ],
      },
      {
        field: 'payment_status',
        label: 'Statut BPI',
        type: 'select',
        operators: ['equals', 'notEquals', 'isNotNull', 'isNull'],
        options: Object.entries(PAYMENT_STATUS_LABELS).map(([value, label]) => ({
          value,
          label,
        })),
      },
      {
        field: 'contact_last_call_at',
        label: 'Dernier appel',
        type: 'date',
        operators: [
          'equals',
          'notEquals',
          'sameDay',
          'before',
          'after',
          'between',
          'isNotNull',
          'isNull',
        ],
      },
      {
        field: 'created_at',
        label: 'Date de création',
        type: 'date',
        operators: [
          'equals',
          'notEquals',
          'sameDay',
          'before',
          'after',
          'between',
          'isNotNull',
          'isNull',
        ],
      },
    ];

    // Filtre "Assigné à" — visible pour tous les rôles
    const userOptions = Object.values(usersMap).map((user) => ({
      value: user.id,
      label: user.full_name || user.email,
    }));

    baseFilters.push({
      field: 'contact.assigned_to',
      label: 'Assigné à',
      type: 'select',
      operators: ['equals', 'notEquals', 'isNotNull', 'isNull'],
      options: userOptions,
    });

    return baseFilters;
  }, [origins, products, usersMap]);

  // Pour Prospects, on utilise csvColumns + csvTransformers pour respecter la visibilité des colonnes
  // Les clés correspondent aux IDs des colonnes du tableau
  const importConfig = useMemo(() => generateImportConfig(PROSPECT_CONFIG), []);

  const csvColumns = useMemo(() => {
    const baseColumns = [
      { key: 'origine' as any, label: 'Origine' },
      { key: 'produit' as any, label: 'Produit' },
      { key: 'nom' as any, label: 'Nom' },
      { key: 'prenom' as any, label: 'Prénom' },
      { key: 'email' as any, label: 'Email' },
      { key: 'telephone' as any, label: 'Téléphone' },
      { key: 'dernier_appel' as any, label: 'Dernier appel' },
      { key: 'bpi_code' as any, label: 'BPI ID' },
      { key: 'payment_status' as any, label: 'Statut BPI' },
    ];

    baseColumns.push({ key: 'assigne_a' as any, label: 'Assigné à' });
    baseColumns.push({ key: 'date' as any, label: 'Date de création' });

    return baseColumns;
  }, []);

  // Transformers qui extraient les données depuis l'item complet
  const csvTransformers = useMemo(
    () => ({
      origine: (_value: any, item?: FormSubmission) => item?.form?.origin?.label || '-',
      produit: (_value: any, item?: FormSubmission) => item?.form?.product?.name || '-',
      nom: (_value: any, item?: FormSubmission) => item?.contact?.last_name || '-',
      prenom: (_value: any, item?: FormSubmission) => item?.contact?.first_name || '-',
      email: (_value: any, item?: FormSubmission) => item?.contact?.email || '-',
      telephone: (_value: any, item?: FormSubmission) => item?.contact?.phone || '-',
      dernier_appel: (_value: any, item?: FormSubmission) => {
        const lastCallAt = (item?.contact as any)?.last_call_at;
        if (!lastCallAt) return '-';
        return new Date(lastCallAt).toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
      bpi_code: (_value: any, item?: FormSubmission) => item?.bpi_code || '-',
      payment_status: (_value: any, item?: FormSubmission) => {
        const status = item?.payment_status as PaymentStatus | undefined;
        return status ? PAYMENT_STATUS_LABELS[status] : '-';
      },
      assigne_a: (_value: any, item?: FormSubmission) =>
        item?.contact?.assigned_user?.full_name || '-',
      date: (_value: any, item?: FormSubmission) =>
        item?.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '-',
    }),
    []
  );

  const entity = useEntityList({
    data: prospects,
    allData, // Toutes les données pour export/import
    loading,
    error,
    create,
    remove,
    removeMany,
    refresh: async () => {
      const data = await refresh();
      await serverPagination.refresh();
      return data;
    },
    fetchFiltered, // ✅ Pour export CSV des données filtrées/sélectionnées
    // Ticket #195 : l'export CSV filtré doit respecter l'onglet BPI et le
    // masquage des expirés (DATA-02), comme la liste et la sélection globale
    requiredFilters: serverPagination.requiredFilters,
    entityName: PROSPECT_CONFIG.entityName,
    entityNamePlural: PROSPECT_CONFIG.entityNamePlural,
    // ✅ Ces searchableFields ne sont pas utilisés en mode server
    // La recherche se fait via fetchPaginatedFromView dans useProspects
    // qui utilise ses propres champs (colonnes aplaties de prospects_view)
    searchableFields: [] as any,
    filterConfigs,
    csvFileName: 'prospects.csv',
    csvColumns,
    csvTransformers,
    importConfig,
    onDeleteSuccess: handleCloseSidePanel,

    // Mode server-side
    mode: 'server',
    total: serverPagination.total,
    totalPages: serverPagination.totalPages,
    currentPage: serverPagination.currentPage,
    itemsPerPage: serverPagination.itemsPerPage,
    onSearchChange: serverPagination.setSearchTerm,
    onPageChange: serverPagination.setCurrentPage,
    onItemsPerPageChange: serverPagination.setItemsPerPage,
    onFiltersChange: serverPagination.setFilters,
  });

  const { handleDeleteClick } = entity;

  // Ouvrir automatiquement le side panel depuis la recherche globale
  // Désactivé en mode server car data est limitée (seulement page courante)
  useOpenFromSearch({
    data: prospects,
    getId: (p) => p.id,
    onOpen: handleOpenSidePanel,
    loading,
    skip: entity.isServerMode,
  });

  const handleUpdateProspect = useCallback(
    async (
      prospectId: string,
      contactId: string,
      updates: {
        contact: Partial<any>;
        submission: { data: Record<string, any> };
      }
    ) => {
      if (!updateContact || !update) return;

      try {
        // 1. Compter les prospects liés à ce contact
        const { count } = await supabase
          .from('form_submissions')
          .select('*', { count: 'exact', head: true })
          .eq('contact_id', contactId);

        // 2. Mettre à jour la soumission (data)
        await update(prospectId, { data: updates.submission.data } as any);

        // 3. Logique de synchronisation intelligente
        if (count === 1) {
          // Contact n'a qu'un prospect → Full update
          await updateContact(contactId, updates.contact);
          toast.success('Prospect et contact mis à jour');
        } else {
          // Contact a plusieurs prospects → Update partiel (assignation + profession/statut étudiant)
          await updateContact(contactId, {
            assigned_to: updates.contact.assigned_to,
            notes: updates.contact.notes,
            // Ces champs sont propres au contact, pas au prospect
            is_not_student: updates.contact.is_not_student,
            socio_professional_category: updates.contact.socio_professional_category,
          });
          toast.success(
            "Prospect mis à jour (contact partagé, seule l'assignation a été modifiée)"
          );
        }

        // 4. Mettre à jour l'état local
        if (selectedProspect?.id === prospectId) {
          setSelectedProspect({
            ...selectedProspect,
            data: updates.submission.data,
            contact: {
              ...selectedProspect.contact!,
              ...updates.contact,
            },
          });
        }

        // 5. Rafraîchir la liste
        await refresh();
        await serverPagination.refresh();
      } catch (error) {
        console.error('Error updating prospect:', error);
        toast.error('Erreur lors de la mise à jour');
        throw error;
      }
    },
    [updateContact, update, supabase, selectedProspect, refresh, serverPagination]
  );

  const handleDeleteFromPanel = useCallback(
    async (id: string) => {
      if (!remove) return;
      try {
        const success = await remove(id);
        if (success) {
          toast.success('Prospect supprimé avec succès');
          // Rafraîchir les données paginées pour mettre à jour la liste
          await serverPagination.refresh();
          handleCloseSidePanel();
        } else {
          toast.error('Erreur lors de la suppression');
        }
      } catch (error) {
        console.error('Error deleting prospect:', error);
        toast.error('Erreur lors de la suppression');
      }
    },
    [remove, handleCloseSidePanel, serverPagination]
  );

  const handleBulkEmail = async () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun prospect sélectionné');
      return;
    }

    // ✅ En mode server-side pagination, récupérer les données par IDs depuis la base
    const selectedIds = Array.from(entity.table.selectedIds);

    try {
      // Utiliser fetchFiltered avec les IDs pour récupérer les prospects complets
      const selectedProspects = await fetchFiltered({ ids: selectedIds });

      if (!selectedProspects || selectedProspects.length === 0) {
        toast.error('Impossible de récupérer les prospects sélectionnés');
        return;
      }

      setSelectedProspectsForEmail(selectedProspects as FormSubmission[]);
      setIsBulkEmailModalOpen(true);
    } catch (err) {
      console.error('Error in handleBulkEmail:', err);
      toast.error("Erreur lors de la préparation de l'envoi");
    }
  };

  const handleBulkAssign = () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun prospect sélectionné');
      return;
    }
    setIsBulkAssignModalOpen(true);
  };

  /**
   * Ouvrir la modal de création de relance en masse (item #16)
   */
  const handleBulkCreateTask = () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun prospect sélectionné');
      return;
    }
    setIsBulkCreateTaskModalOpen(true);
  };

  /**
   * Assigner un utilisateur à tous les prospects sélectionnés.
   * Item #5 retours client : l'assignation est portée par le contact lié,
   * pas par la form_submission. On résout d'abord les contact_ids depuis
   * form_submissions, puis on met à jour contacts.assigned_to en masse.
   */
  const handleBulkAssignUser = async (userId: string) => {
    const submissionIds = Array.from(entity.table.selectedIds);

    try {
      // 1. Résoudre les contact_ids depuis les form_submissions sélectionnées
      const { data: submissions, error: fetchError } = await supabase
        .from('form_submissions')
        .select('contact_id')
        .in('id', submissionIds);

      if (fetchError) throw fetchError;

      const contactIds = Array.from(
        new Set(
          (submissions || [])
            .map((s: { contact_id: string | null }) => s.contact_id)
            .filter((id): id is string => Boolean(id))
        )
      );

      if (contactIds.length === 0) {
        toast.error('Impossible de résoudre les contacts liés');
        return;
      }

      // 2. Mise à jour en masse de contacts.assigned_to
      const { error: updateError } = await supabase
        .from('contacts')
        .update({ assigned_to: userId })
        .in('id', contactIds);

      if (updateError) throw updateError;

      toast.success(`${submissionIds.length} prospect(s) assigné(s) avec succès`);

      await refresh();
      await serverPagination.refresh();
    } catch (err) {
      console.error("Erreur lors de l'assignation en masse:", err);
      toast.error("Erreur lors de l'assignation");
      throw err;
    }
  };

  /**
   * Sélectionner tous les résultats correspondant aux filtres actuels (Feature 4.4)
   */
  const handleSelectAllResults = async () => {
    if (!fetchAllFilteredIds) return;

    setIsLoadingAllIds(true);
    try {
      const allIds = await fetchAllFilteredIds({
        search: serverPagination.searchTerm,
        filters: serverPagination.filters,
        filterMode: serverPagination.filterMode,
        requiredFilters: serverPagination.requiredFilters,
      });

      // Mettre à jour selectedIds avec tous les IDs
      entity.table.setSelectedIds(new Set(allIds));
      setIsAllResultsSelected(true);
      toast.success(`${allIds.length} ${PROSPECT_CONFIG.entityNamePlural} sélectionnés`);
    } catch (err) {
      console.error('Erreur sélection globale:', err);
      toast.error('Erreur lors de la sélection');
    } finally {
      setIsLoadingAllIds(false);
    }
  };

  const bpiFilterConditions = useMemo(() => {
    if (bpiFilter === 'without_bpi') {
      // Demandes entrantes : formulaires sans génération BPI (exclut les pré-inscriptions
      // même si leur bpi_code n'est pas encore résolu)
      return [
        { field: 'bpi_code', operator: 'isNull' as const, value: '' },
        { field: 'form.generates_bpi', operator: 'equals' as const, value: 'false' },
      ];
    }
    if (bpiFilter === 'with_bpi') {
      // Pré-inscriptions : tous les formulaires generates_bpi=true, qu'ils aient
      // déjà un bpi_code ou non (cas où le BPI n'est pas encore résolu)
      return [{ field: 'form.generates_bpi', operator: 'equals' as const, value: 'true' }];
    }
    // crew_join_requests : données gérées par CrewJoinRequestsTable, pas de filtre prospects_view
    return [];
  }, [bpiFilter]);

  // DATA-02 : on fusionne le filtre d'onglet BPI et le masquage des expirés en un
  // seul jeu de requiredFilters (setServerRequiredFilters remplace tout le jeu).
  // notEqualsOrNull (et non notEquals) pour conserver les prospects sans BPI dont
  // payment_status est NULL — sinon ils disparaîtraient des "Demandes entrantes".
  const requiredFilterConditions = useMemo(
    () => [
      ...bpiFilterConditions,
      ...(showExpired
        ? []
        : [{ field: 'payment_status', operator: 'notEqualsOrNull' as const, value: 'expired' }]),
      ...(showAlreadyInCrew
        ? []
        : [{ field: 'already_in_crew', operator: 'equals' as const, value: 'false' }]),
    ],
    [bpiFilterConditions, showExpired, showAlreadyInCrew]
  );

  const { setRequiredFilters: setServerRequiredFilters } = serverPagination;

  useEffect(() => {
    setServerRequiredFilters(requiredFilterConditions);
  }, [setServerRequiredFilters, requiredFilterConditions]);

  const currentSavedSearchDefinition = useMemo(
    () =>
      createSavedSearchDefinition({
        searchTerm: entity.table.searchTerm,
        filters: entity.table.activeFilters,
        requiredFilters: requiredFilterConditions,
        filterMode: entity.table.filterMode,
        sortField: serverPagination.sortField,
        sortDirection: serverPagination.sortDirection,
        columnVisibility: entity.table.columnVisibility,
        pageSize: entity.table.itemsPerPage,
      }),
    [
      entity.table.activeFilters,
      entity.table.columnVisibility,
      entity.table.filterMode,
      entity.table.itemsPerPage,
      entity.table.searchTerm,
      requiredFilterConditions,
      serverPagination.sortDirection,
      serverPagination.sortField,
    ]
  );

  const defaultSavedSearchDefinition = useMemo(
    () =>
      createSavedSearchDefinition({
        searchTerm: '',
        filters: [],
        requiredFilters: [
          { field: 'payment_status', operator: 'notEqualsOrNull', value: 'expired' },
          { field: 'already_in_crew', operator: 'equals', value: 'false' },
        ],
        filterMode: 'and',
        sortField: null,
        sortDirection: 'desc',
        columnVisibility: {},
        pageSize: 25,
      }),
    []
  );

  function applySavedSearchDefinition(definition: SavedSearchDefinition) {
    const generatesBpi = definition.requiredFilters.find(
      (filter) => filter.field === 'form.generates_bpi' && filter.operator === 'equals'
    )?.value;
    setBpiFilter(
      generatesBpi === 'true' ? 'with_bpi' : generatesBpi === 'false' ? 'without_bpi' : 'all'
    );
    setShowExpired(
      !definition.requiredFilters.some(
        (filter) => filter.field === 'payment_status' && filter.operator === 'notEqualsOrNull'
      )
    );
    setShowAlreadyInCrew(
      !definition.requiredFilters.some((filter) => filter.field === 'already_in_crew')
    );
    entity.table.setSearchTerm(definition.searchTerm);
    entity.table.setActiveFilters(definition.filters, definition.filterMode);
    entity.table.setColumnVisibility(definition.columnVisibility);
    entity.table.setItemsPerPage(definition.pageSize);
    serverPagination.setSort(definition.sort?.field || null, definition.sort?.direction);
    entity.table.clearSelection();
  }

  useSearchSessionMemory({
    entityType: 'prospects',
    currentDefinition: currentSavedSearchDefinition,
    filterConfigs: entity.filterConfigs,
    onRestore: applySavedSearchDefinition,
  });

  // Reset de la sélection quand les filtres, la recherche ou l'onglet changent.
  // Ticket #195 : clearSelection() aussi, pas seulement le booléen — sinon les
  // IDs de l'ancien périmètre restaient sélectionnés (barre d'actions
  // « N sélectionnés » sur tableau vierge, export CSV faux).
  const { clearSelection } = entity.table;
  useEffect(() => {
    setIsAllResultsSelected(false);
    clearSelection();
  }, [
    serverPagination.filters,
    serverPagination.searchTerm,
    bpiFilter,
    showExpired,
    showAlreadyInCrew,
    clearSelection,
  ]);

  // Calcul des groupes BPI et attribution des couleurs
  // Seuls les bpi_code présents 2+ fois sur la page courante sont colorisés
  const bpiColorMap = useMemo(() => {
    // Couleurs distinctes pour identifier visuellement les groupes BPI
    const BPI_GROUP_COLORS = [
      'bg-blue-50',
      'bg-green-50',
      'bg-amber-50',
      'bg-purple-50',
      'bg-pink-50',
      'bg-cyan-50',
      'bg-orange-50',
      'bg-rose-50',
    ];

    const colorMap = new Map<string, string>();

    // Compter les occurrences de chaque bpi_code
    const bpiCounts = new Map<string, number>();
    prospects.forEach((prospect) => {
      if (prospect.bpi_code) {
        bpiCounts.set(prospect.bpi_code, (bpiCounts.get(prospect.bpi_code) || 0) + 1);
      }
    });

    // Attribuer une couleur aux bpi_code qui apparaissent 2+ fois
    let colorIndex = 0;
    bpiCounts.forEach((count, bpiCode) => {
      if (count >= 2) {
        colorMap.set(bpiCode, BPI_GROUP_COLORS[colorIndex % BPI_GROUP_COLORS.length]);
        colorIndex++;
      }
    });

    return colorMap;
  }, [prospects]);

  // Fonction pour obtenir la classe CSS de coloration d'une ligne
  const getRowClassName = useCallback(
    (row: FormSubmission) => {
      if (row.bpi_code) {
        return bpiColorMap.get(row.bpi_code);
      }
      return undefined;
    },
    [bpiColorMap]
  );

  const columns: ColumnDef<FormSubmission>[] = useMemo(() => {
    const baseColumns: ColumnDef<FormSubmission>[] = [
      {
        id: 'select',
        size: 60,
        header: ({ table }) => (
          <CheckboxCell
            row={
              {
                getIsSelected: () => table.getIsAllRowsSelected(),
                toggleSelected: () => table.toggleAllRowsSelected(),
              } as any
            }
          />
        ),
        cell: ({ row }) => <CheckboxCell row={row} />,
      },
      {
        id: 'origine',
        header: 'Origine',
        accessorFn: (row) => row.form?.origin?.label,
        cell: ({ row }) => {
          const originLabel = row.original.form?.origin?.label;
          return originLabel ? (
            <div className="flex items-center">
              <Badge variant="secondary">{originLabel}</Badge>
            </div>
          ) : (
            <span className="text-sm text-zinc-500">-</span>
          );
        },
        enableSorting: true,
      },
      {
        id: 'produit',
        header: 'Produit',
        accessorFn: (row) => row.form?.product?.name,
        cell: ({ row }) => <TableCell>{row.original.form?.product?.name || '-'}</TableCell>,
        enableSorting: true,
      },
      {
        id: 'nom',
        header: 'Nom',
        accessorFn: (row) => row.contact?.last_name,
        cell: ({ row }) => (
          <TableCell className="font-semibold">{row.original.contact?.last_name || '-'}</TableCell>
        ),
        enableSorting: true,
      },
      {
        id: 'prenom',
        header: 'Prénom',
        accessorFn: (row) => row.contact?.first_name,
        cell: ({ row }) => <TableCell>{row.original.contact?.first_name || '-'}</TableCell>,
        enableSorting: true,
      },
      {
        id: 'email',
        header: 'Email',
        accessorFn: (row) => row.contact?.email,
        cell: ({ row }) => <TableCell>{row.original.contact?.email || '-'}</TableCell>,
        enableSorting: true,
      },
      {
        id: 'telephone',
        header: 'Téléphone',
        accessorFn: (row) => row.contact?.phone,
        cell: ({ row }) => <TableCell>{row.original.contact?.phone || '-'}</TableCell>,
        enableSorting: true,
      },
      {
        id: 'dernier_appel',
        header: 'Dernier appel',
        accessorFn: (row) => (row.contact as any)?.last_call_at,
        cell: ({ row }) => {
          const lastCallAt = (row.original.contact as any)?.last_call_at;
          if (!lastCallAt) return <span className="text-sm text-zinc-400">-</span>;
          const date = new Date(lastCallAt);
          return (
            <TableCell className="text-zinc-600">
              {date.toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TableCell>
          );
        },
        enableSorting: true,
        size: 150,
      },
      {
        id: 'bpi_code',
        header: 'BPI ID',
        accessorFn: (row) => row.bpi_code,
        cell: ({ row }) => (
          <TableCell className="font-mono text-sm">{row.original.bpi_code || '-'}</TableCell>
        ),
        enableSorting: true,
        size: 130,
      },
      {
        id: 'payment_status',
        header: 'Statut BPI',
        accessorFn: (row) => row.payment_status,
        cell: ({ row }) => (
          <PaymentBadgeCell status={row.original.payment_status as PaymentStatus | null} />
        ),
        enableSorting: true,
        size: 150,
      },
      {
        id: 'date',
        header: 'Date de création',
        accessorFn: (row) => row.created_at,
        cell: ({ row }) => {
          const date = new Date(row.original.created_at);
          return <TableCell>{date.toLocaleDateString('fr-FR')}</TableCell>;
        },
        enableSorting: true,
      },
    ];

    // Colonne "Assigné à" — visible pour tous les rôles
    baseColumns.push({
      id: 'assigne_a',
      header: 'Assigné à',
      accessorFn: (row) => row.contact?.assigned_user?.full_name,
      cell: ({ row }) => (
        <TableCell>{row.original.contact?.assigned_user?.full_name || '-'}</TableCell>
      ),
      // 'assigne_a' n'est pas une colonne de prospects_view : le tri serveur
      // renverrait un 400 (cf. sort-columns / resolveSortColumn).
      enableSorting: false,
    });

    // Ajouter la colonne actions
    baseColumns.push({
      id: 'actions',
      cell: ({ row }) => (
        <ActionsCell
          onView={() => handleOpenSidePanel(row.original)}
          onDelete={() =>
            handleDeleteClick(row.original.id, row.original.contact?.email || 'ce prospect')
          }
          canDelete={canDeleteEntity('prospect')}
        />
      ),
    });

    return baseColumns;
  }, [handleOpenSidePanel, handleDeleteClick, canDeleteEntity]);

  const columnOptions = useMemo(() => {
    const baseOptions = [
      {
        id: 'origine',
        label: 'Origine',
        visible: entity.table.columnVisibility.origine !== false,
      },
      {
        id: 'produit',
        label: 'Produit',
        visible: entity.table.columnVisibility.produit !== false,
      },
      { id: 'nom', label: 'Nom', visible: entity.table.columnVisibility.nom !== false },
      { id: 'prenom', label: 'Prénom', visible: entity.table.columnVisibility.prenom !== false },
      { id: 'email', label: 'Email', visible: entity.table.columnVisibility.email !== false },
      {
        id: 'telephone',
        label: 'Téléphone',
        visible: entity.table.columnVisibility.telephone !== false,
      },
      {
        id: 'dernier_appel',
        label: 'Dernier appel',
        visible: entity.table.columnVisibility.dernier_appel !== false,
      },
      {
        id: 'bpi_code',
        label: 'BPI ID',
        visible: entity.table.columnVisibility.bpi_code !== false,
      },
      {
        id: 'payment_status',
        label: 'Statut BPI',
        visible: entity.table.columnVisibility.payment_status !== false,
      },
      {
        id: 'date',
        label: 'Date de création',
        visible: entity.table.columnVisibility.date !== false,
      },
    ];

    // Colonne "Assigné à" — visible pour tous les rôles
    baseOptions.push({
      id: 'assigne_a',
      label: 'Assigné à',
      visible: entity.table.columnVisibility.assigne_a !== false,
    });

    return baseOptions;
  }, [entity.table.columnVisibility]);

  // ✅ Ne plus conditionner l'affichage sur loading
  // Le tableau affichera son propre indicateur de chargement

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Erreur de chargement"
          message="Impossible de charger les prospects"
          onRetry={async () => {
            await refresh();
            await serverPagination.refresh();
          }}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      header={
        <>
          <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Prospects' }]} />
          <div className="w-full h-px bg-zinc-200" />
        </>
      }
      sidePanel={
        selectedProspect && (
          <ProspectDetailPanel
            isOpen={isSidePanelOpen}
            onClose={handleCloseSidePanel}
            prospect={selectedProspect}
            teammate={teammate}
            onUpdate={handleUpdateProspect}
            onDelete={handleDeleteFromPanel}
          />
        )
      }
    >
      {/* Onglets de filtrage par présence de code BPI */}
      <div className="flex items-center justify-between gap-2 px-6 py-3 bg-zinc-50 border-b border-zinc-200">
        <div className="flex gap-2">
          {(
            [
              { value: 'all', label: 'Tous' },
              { value: 'without_bpi', label: 'Demandes entrantes' },
              { value: 'with_bpi', label: 'Pré-inscriptions' },
              ...(role !== 'user'
                ? [{ value: 'crew_join_requests' as const, label: 'Ajout coéquipier' }]
                : []),
            ] as const
          ).map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setBpiFilter(value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                bpiFilter === value
                  ? 'bg-white text-zinc-900 font-medium shadow-sm'
                  : 'text-zinc-600 hover:text-zinc-900 hover:bg-white/50'
              }`}
            >
              {label}
              {bpiFilter === value &&
                (() => {
                  const count =
                    value === 'crew_join_requests' ? crewJoinRequestsCount : serverPagination.total;
                  return count > 0 ? (
                    <span className="ml-1.5 text-xs opacity-70">({count})</span>
                  ) : null;
                })()}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          {/* #234 : afficher/masquer les personnes déjà en équipage (masquées par défaut) */}
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-zinc-600">
            <input
              type="checkbox"
              checked={showAlreadyInCrew}
              onChange={(e) => setShowAlreadyInCrew(e.target.checked)}
              className="size-4 cursor-pointer rounded border-zinc-300 accent-zinc-700"
            />
            Afficher les déjà en équipage
          </label>

          {/* DATA-02 : afficher/masquer les BPI auto-expirés (masqués par défaut) */}
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-zinc-600">
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
              className="size-4 cursor-pointer rounded border-zinc-300 accent-zinc-700"
            />
            Afficher les expirés
          </label>
        </div>
      </div>

      {bpiFilter === 'crew_join_requests' ? (
        <PageContent
          title="Ajout coéquipier"
          description="Demandes d'ajout de coéquipier soumises via le formulaire public"
        >
          <CrewJoinRequestsTable onCountChange={setCrewJoinRequestsCount} />
        </PageContent>
      ) : (
        <PageContent
          title="Prospects"
          description="Gérez les soumissions de formulaires et prospects"
          actions={
            <ActionButtons
              leadingControls={
                <SavedSearchToolbar
                  entityType="prospects"
                  currentDefinition={currentSavedSearchDefinition}
                  defaultDefinition={defaultSavedSearchDefinition}
                  filterConfigs={entity.filterConfigs}
                  visibleColumnCount={columnOptions.filter((column) => column.visible).length}
                  onApply={applySavedSearchDefinition}
                />
              }
              searchValue={entity.table.searchTerm}
              onSearchChange={entity.table.setSearchTerm}
              onDownload={canExportData('prospect') ? entity.handleExportCSV : undefined}
              onUpload={
                canImportData('prospect') ? () => entity.setIsImportModalOpen(true) : undefined
              }
              onFilter={() => entity.setIsFiltersModalOpen(true)}
              onColumns={() => entity.setIsColumnModalOpen(true)}
              onCreate={
                canCreateEntity('prospect') ? () => entity.setIsCreateModalOpen(true) : undefined
              }
              activeFiltersCount={entity.table.activeFilters.length}
            />
          }
          footer={
            <Pagination
              currentPage={entity.table.currentPage}
              totalPages={entity.table.totalPages}
              itemsPerPage={entity.table.itemsPerPage}
              totalItems={entity.table.totalItems}
              displayedItems={entity.table.displayedItemsCount}
              onPageChange={entity.table.setCurrentPage}
              onItemsPerPageChange={entity.table.setItemsPerPage}
              itemsPerPageOptions={[10, 25, 50, 100]}
            />
          }
        >
          <ActiveFilterChips
            filters={entity.table.activeFilters}
            filterConfigs={entity.filterConfigs}
            filterMode={entity.table.filterMode}
            onChange={entity.table.setActiveFilters}
          />

          {/* Bandeau de sélection globale (Feature 4.4) */}
          <SelectAllBanner
            selectedCount={entity.table.selectedCount}
            pageItemCount={prospects.length}
            allPageSelected={entity.table.isAllPageSelected(prospects)}
            totalFilteredCount={serverPagination.total}
            onSelectAll={handleSelectAllResults}
            onClearSelection={() => {
              entity.table.clearSelection();
              setIsAllResultsSelected(false);
            }}
            entityNamePlural={PROSPECT_CONFIG.entityNamePlural}
            isAllSelected={isAllResultsSelected}
            isLoading={isLoadingAllIds}
          />
          <EnterpriseTable
            data={entity.table.filteredData}
            columns={columns}
            onRowSelectionChange={entity.table.setSelectedItems}
            columnVisibility={entity.table.columnVisibility}
            onColumnVisibilityChange={entity.table.setColumnVisibility}
            clearSelectionTrigger={entity.table.clearSelectionTrigger}
            pageSize={entity.table.itemsPerPage}
            currentPage={entity.table.currentPage}
            onPageChange={entity.table.setCurrentPage}
            manualPagination={entity.isServerMode}
            getRowClassName={getRowClassName}
            loading={loading}
            // ✅ Tri server-side (tri sur l'ensemble du dataset)
            manualSorting={entity.isServerMode}
            onSortChange={serverPagination.setSort}
            activeSortField={serverPagination.sortField}
            activeSortDirection={serverPagination.sortDirection}
            // ✅ Nouveau système de sélection multi-pages
            selectedIds={entity.table.selectedIds}
            onToggleSelection={entity.table.toggleItemSelection}
            onToggleAllPage={entity.table.toggleAllPageSelection}
          />
        </PageContent>
      )}

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={(newColumns) => {
          const newVisibility: Record<string, boolean> = {};
          newColumns.forEach((col) => {
            newVisibility[col.id] = col.visible !== false;
          });
          entity.table.setColumnVisibility(newVisibility);
        }}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateProspectModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            await entity.handleCreate(data);
          }}
        />
      )}

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data: any) => {
          // ✅ Utiliser la fonction create qui gère contact + form_submission
          const prospect = await create({
            form_id: data.form_id,
            contact: {
              first_name: data.first_name || '',
              last_name: data.last_name || '',
              email: data.email || '',
              phone: data.phone || '',
            },
          });

          if (!prospect) {
            throw new Error('Erreur lors de la création du prospect');
          }

          return prospect;
        }}
        onUpdate={async (_id, _data: any) => {
          // L'update de prospects n'est pas géré via CSV, retourner true
          return true;
        }}
        existingData={allData}
        entityName={PROSPECT_CONFIG.entityName}
        entityNamePlural={PROSPECT_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        onConfirm={entity.confirmDelete}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${PROSPECT_CONFIG.entityNamePlural}`
            : `Supprimer le ${PROSPECT_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Voulez-vous vraiment supprimer ${entity.table.selectedCount} ${PROSPECT_CONFIG.entityName}(s) ?`
            : `Voulez-vous vraiment supprimer le ${PROSPECT_CONFIG.entityName} "${entity.deleteConfirm.name}" ?`
        }
      />

      <SendBulkEmailModal
        isOpen={isBulkEmailModalOpen}
        onClose={() => {
          setIsBulkEmailModalOpen(false);
          setSelectedProspectsForEmail([]);
        }}
        entities={selectedProspectsForEmail}
        entityType="prospect"
        onSuccess={async () => {
          await refresh();
          await serverPagination.refresh();
          setSelectedProspectsForEmail([]);
        }}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedCount > 0 && (
        <BulkActionsBar
          count={entity.table.selectedCount}
          entityNamePlural={PROSPECT_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onEmail={handleBulkEmail}
          onAssign={handleBulkAssign}
          onCreateTask={handleBulkCreateTask}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={canSendEmails()}
          showAssignAction={canManageAssignments()}
          showCreateTaskAction={true}
          canDelete={canDeleteBulk('prospect')}
          canExport={canExportData('prospect')}
        />
      )}

      {/* Modale d'assignation en masse */}
      {isBulkAssignModalOpen && (
        <BulkAssignUserModal
          isOpen={isBulkAssignModalOpen}
          onClose={() => setIsBulkAssignModalOpen(false)}
          count={entity.table.selectedCount}
          entityNamePlural={PROSPECT_CONFIG.entityNamePlural}
          onAssign={handleBulkAssignUser}
        />
      )}

      {/* Modale de création de relance en masse (item #16) */}
      {isBulkCreateTaskModalOpen && (
        <BulkCreateTaskModal
          isOpen={isBulkCreateTaskModalOpen}
          onClose={() => setIsBulkCreateTaskModalOpen(false)}
          entityType="prospect"
          entityIds={Array.from(entity.table.selectedIds)}
          entityNamePlural={PROSPECT_CONFIG.entityNamePlural}
          onSuccess={() => {}}
        />
      )}
    </PageLayout>
  );
}
